import {useCallback, useRef, useState, useLayoutEffect} from 'preact/hooks';
import {RefObject} from 'preact';

import {LoadingPlaceholder} from 'components/LoadingPlaceholder';
import {useTheme} from 'utilities/color';
import {PropsWithChildren} from 'utilities/props';
import {useListener, useResizeObserver} from 'utilities/dom';

import styles from './PromiseShell.css';

interface Props {
  isLoading: boolean;
  promiseIntersectionRef: RefObject<HTMLDivElement>;
}

const NO_DEBOUNCE = 0;

function setElementTransitionHeight(
  element?: HTMLElement | null,
  height?: string,
  maxHeight?: string,
) {
  if (!element || !height) {
    return null;
  }
  element.style.height = height;
  element.style.maxHeight = maxHeight ?? height;

  return {
    height,
    maxHeight: height ?? maxHeight,
  };
}

/**
 * A shell component that transitions between loading and content states.
 */
export function PromiseShell({
  isLoading,
  children,
  promiseIntersectionRef,
}: PropsWithChildren<Props>) {
  const {theme} = useTheme();
  const [transition, setTransition] = useState(true);

  const memoizedDimensions = useRef<{
    maxHeight: string;
    height: string;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  const updateHeight = useCallback((rect: DOMRectReadOnly) => {
    memoizedDimensions.current =
      setElementTransitionHeight(containerRef.current, `${rect.height}px`) ??
      memoizedDimensions.current;
  }, []);

  const observeApi = useResizeObserver(targetRef, updateHeight, NO_DEBOUNCE);
  useListener(
    'transitionend',
    (event: TransitionEvent) => {
      if (
        event.target === containerRef.current &&
        event.propertyName === 'height'
      ) {
        observeApi.current?.unobserve();
        setElementTransitionHeight(containerRef.current, 'auto', 'inherit');
      }
    },
    [],
    containerRef.current!,
  );

  useLayoutEffect(() => {
    observeApi.current?.observe();

    setElementTransitionHeight(
      containerRef.current,
      memoizedDimensions.current?.height,
      memoizedDimensions.current?.maxHeight,
    );

    requestAnimationFrame(() => {
      setTransition(isLoading);
    });
  }, [isLoading, observeApi, updateHeight]);

  return (
    <div className={styles['ShopPromise-SmoothContainer']} ref={containerRef}>
      <div
        ref={targetRef}
        className={[
          styles.Content,
          styles.SwitchContainer,
          transition ? styles['--IsLoading'] : '',
        ].join(' ')}
      >
        <div className={styles.Loader}>
          <LoadingPlaceholder theme={theme} />
        </div>
        <div className={styles.Promise} ref={promiseIntersectionRef}>
          {children}
        </div>
      </div>
    </div>
  );
}
